import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["inventory", "timing", "written", "draw", "refresh", "fallback", "status", "save"];

  get saveButtons() {
    return Array.from(this.element.elements).filter((element) => element.matches('[data-dose-entry-target~="save"]'));
  }

  connect() {
    this.fallbackTargets.forEach((element) => {
      element.hidden = true;
    });
    if (this.element.dataset.previewPending) this.schedule(0);
  }

  disconnect() {
    clearTimeout(this.timer);
    this.version = (this.version || 0) + 1;
    this.request?.abort();
  }

  edited(event) {
    if (event.target.name === "dose[dose_amount]") this.schedule();
  }

  // A source card is a submit button that carries its tap, so the server applies
  // the pick. The tap rides along with the recalculation until one lands.
  pick(event) {
    event.preventDefault();
    this.pendingAction = event.currentTarget.value;
    this.schedule(0);
  }

  // The reserve unfolds in place, and the field it sets keeps it unfolded
  // through every recalculation the server renders afterwards.
  reveal(event) {
    event.preventDefault();
    this.inventoryTarget.querySelector('input[name="dose[reserve_shown]"]').value = "1";
    this.inventoryTarget.querySelectorAll("[data-dose-source-reserve]").forEach((card) => {
      card.hidden = false;
    });
    event.currentTarget.hidden = true;
  }

  changed(event) {
    if (
      ["dose[dose_unit]", "dose[syringe_size_ml]", "dose[logged_on]", "dose[logged_at_time]"].includes(
        event.target.name
      )
    )
      this.schedule(0);
  }

  schedule(delay = 200) {
    this.element.dataset.previewPending = "true";
    clearTimeout(this.timer);
    this.request?.abort();
    this.version = (this.version || 0) + 1;
    this.saveButtons.forEach((button) => {
      button.disabled = true;
    });
    this.statusTarget.hidden = false;
    this.statusTarget.classList.remove("sr-only");
    this.statusTarget.textContent = "Updating draw guide…";
    if (this.hasInventoryTarget) {
      this.inventoryTarget.setAttribute("aria-busy", "true");
      this.inventoryTarget.classList.add("opacity-50");
    }
    this.timer = setTimeout(() => this.refresh(this.version), delay);
  }

  async refresh(version) {
    const request = new AbortController();
    this.request = request;
    const action = this.pendingAction;
    const data = new FormData(this.element, this.refreshTarget);
    data.set(this.refreshTarget.name, action || this.refreshTarget.value);
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      request.abort();
    }, 10000);
    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        body: data,
        headers: { Accept: "text/html" },
        signal: request.signal,
      });
      if ((!response.ok && response.status !== 422) || response.redirected) throw new Error("Draw guide unavailable");
      const document = new DOMParser().parseFromString(await response.text(), "text/html");
      const replacement = document.querySelector('[data-dose-entry-target="inventory"]');
      const timing = document.querySelector('[data-dose-entry-target="timing"]');
      const written = document.querySelector('[data-dose-entry-target="written"]');
      const draw = document.querySelector('[data-dose-entry-target="draw"]');
      if ((this.hasInventoryTarget && !replacement) || (this.hasTimingTarget && !timing))
        throw new Error("Dose preview missing");
      if ((this.hasWrittenTarget && !written) || (this.hasDrawTarget && !draw)) throw new Error("Dose guide missing");
      if (version !== this.version) return;
      const focused = window.document.activeElement;
      const restoreFocus = this.hasInventoryTarget && this.inventoryTarget.contains(focused) ? focused.id : null;
      if (this.hasInventoryTarget) this.inventoryTarget.replaceWith(replacement);
      if (this.hasWrittenTarget) this.writtenTarget.replaceWith(written);
      if (this.hasDrawTarget) this.drawTarget.replaceWith(draw);
      this.dispatch("refreshed");
      if (this.pendingAction === action) this.pendingAction = null;
      if (this.hasTimingTarget) this.timingTarget.replaceWith(timing);
      if (restoreFocus) window.document.getElementById(restoreFocus)?.focus({ preventScroll: true });
      this.statusTarget.textContent = "Draw guide updated.";
      this.statusTarget.classList.add("sr-only");
      this.fallbackTargets.forEach((element) => {
        element.hidden = true;
      });
    } catch (error) {
      if ((error.name === "AbortError" && !timedOut) || version !== this.version) return;
      this.pendingAction = null;
      this.dispatch("failed");
      this.statusTarget.textContent = "The draw guide could not update. Use Update draw guide to try again.";
      this.fallbackTargets.forEach((element) => {
        element.hidden = false;
      });
    } finally {
      clearTimeout(timeout);
      if (version === this.version) {
        delete this.element.dataset.previewPending;
        this.saveButtons.forEach((button) => {
          button.disabled = false;
        });
        if (this.hasInventoryTarget) {
          this.inventoryTarget.removeAttribute("aria-busy");
          this.inventoryTarget.classList.remove("opacity-50");
        }
      }
    }
  }
}
